<template>
  <Layout>
    <div class="page-header">
      <div class="page-title">
        <h2>{{$t('programs_title')}}</h2>
        <h5>{{$t('create_manage_programs')}}</h5>
      </div>

      <div class="actions">
        <ProgramFilters />
        <ExpandableSearch />

        <button class="justify-end-add-btn no-text-select" @click="handleProgramCreate">
          <img src="@/assets/images/plus.svg" />
          <span>{{$t('add_new')}}</span>
        </button>
      </div>
    </div>


    <div class="filter-wrapper">
      <div class="justify-end-div">
        <button class="generator-info-button" :class="genStatus?.status" v-if="checkGeneratorButton" @click="() => generativeProgressModalVisible = true">
          <span>{{genStatus?.status === 'completed' ? 'Completed' : genStatus?.status === 'in_progress' ? 'In Progress' : 'Pending'}}</span>
        </button>
      </div>
    </div>

    <div v-if="!isLoading && programData.data && programData.data.length === 0" class="no-results">
      <p>{{ $t('no_programs_found') }}</p>
    </div>

    <div class="program-list-grid" v-else>
        <ProgramCard
            v-for="item in programData.data"
            :is-loading="isLoading"
            :image="item.avatar"
            :title="item.name"
            :description="item.shortDescription"
            :remaining-days="item.remainingDay"
            :assigned-at="item.assignedAt"
            :tags="item.tags.map((x) => x.name)"
            :to="`/program/${item.programId}`"
            :remaining="item.remaining"
            :start-at="item.startAt"
            :finish-at="item.finishAt"
            :is-publish="item.isPublish"
            :companies-assigned-count="item.companiesAssignedCount"
            :user-assigned-count="item.userAssignedCount"
        />

      <ProgramCard v-if="isLoading" />
      <ProgramCard v-if="isLoading" />
      <ProgramCard v-if="isLoading" />
      <ProgramCard v-if="isLoading" />
    </div>


    <div class="pagination-align-center">
      <Pagination :current-page="1" :total-items="programData.total" :per-page="20" :on-page-change="handlePage"/>
    </div>


    <CreateProgramModal
        :visible="createProgramModalVisible"
        :on-action="handleCreateAction"
        :on-hide="handleHideCreateProgram"
    />

    <GenerativeProgramModal
        :visible="generativeModalVisible"
        :on-hide="handleHideGenerativeProgram"
        :on-action="handleGeneratorAction"
        :action-processing="generativeProgramProcessing"
    />

    <GeneratingProgressModal
      :visible="generativeProgressModalVisible"
      :on-hide="() => generativeProgressModalVisible = false"
      :gen-status="genStatus"
    />

    <TransformProgramModal
      :visible="transformProgramModalVisible"
      :on-hide="() => transformProgramModalVisible = false"
      :on-action="handleGeneratorAction"
    />
  </Layout>
</template>

<script>
import ExpandableSearch from "@/components/ExpandableSearch.vue";
import Layout from "@/components/layouts/Layout.vue";
import ProgramCard from "@/components/ProgramCard.vue";
import programService from "@/service/programService";
import Pagination from "@/components/Pagination.vue";
import ProgramFilters from "@/components/tables/Program/ProgramFilters.vue";
import CreateProgramModal from "@/components/modals/CreateProgramModal.vue";
import GenerativeProgramModal from "@/components/modals/GenerativeProgramModal.vue";
import aiSocket from "@/utils/aiSocket";
import aiService from "@/service/aiService";
import {handleErrorResponse} from "@/utils/utils";
import GeneratingProgressModal from "@/components/modals/GeneratingProgressModal.vue";
import TransformProgramModal from "@/components/modals/TransformProgramModal.vue";

export default {
  components: {
    TransformProgramModal,
    GeneratingProgressModal,
    GenerativeProgramModal,
    CreateProgramModal,
    ProgramFilters,
    Pagination,
    ProgramCard,
    Layout,
    ExpandableSearch
  },
  data() {
    return {
      aiSocket,
      isLoading: true,
      programData: {},
      currentPage: 1,
      createProgramModalVisible: false,
      generativeModalVisible: false,
      generativeProgressModalVisible: false,
      transformProgramModalVisible: false,
      generativeProgramProcessing: false,
      genStatus: {},
    }
  },
  watch: {
    $route() {
      this.handlePage(1);
      this.checkUrlParams();
    },
    'aiSocket.state.genStatus': {
      handler: function (val) {
        this.genStatus = val;
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    aiSocket.connect();
    this.handleJoinGenerator();
    this.checkUrlParams();
  },
  unmounted() {
    aiSocket.disconnect();
    this.handleLeaveGenerator();
  },
  methods: {
    handlePage(page) {
      if (this.isLoading && this.currentPage !== 1) return;
      this.isLoading = true;
      this.currentPage = page;

      const routeQuery = this.$route.query;
      const tempFilters = [];

      if (routeQuery.searchText) {
        tempFilters.push({
          key: 'searchText',
          value: routeQuery.searchText
        });
      }

      if (routeQuery.startAt) {
        tempFilters.push({
          key: 'startAt',
          value: routeQuery.startAt
        });
      }

      if (routeQuery.startAtEnd) {
        tempFilters.push({
          key: 'startAtEnd',
          value: routeQuery.startAtEnd
        });
      }

      if (routeQuery.finishAt) {
        tempFilters.push({
          key: 'finishAt',
          value: routeQuery.finishAt
        });
      }

      if (routeQuery.finishAtEnd) {
        tempFilters.push({
          key: 'finishAtEnd',
          value: routeQuery.finishAtEnd
        });
      }

      if (routeQuery.isPublish) {
        if (routeQuery.isPublish === "1") {
          tempFilters.push({
            key: 'isPublish',
            value: "1"

          })
        } else if (routeQuery.isPublish === "0") {
          tempFilters.push({
            key: 'isPublish',
            value: "0"
          })
        }
      }

      programService.getPrograms(page, tempFilters).then(res => {
        this.programData = res.data.data;
      }).catch((error) => {
        handleErrorResponse(error, this.$snackbar);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    handleProgramCreate() {
      this.createProgramModalVisible = true;
    },
    handleHideCreateProgram() {
      this.createProgramModalVisible = false;
    },
    handleCreateAction(action) {
      if (action === 'manual') {
        this.$router.push('/program/new');
      } else if (action === 'genAI') {
        this.generativeModalVisible = true;
      } else if (action === 'transform') {
        this.transformProgramModalVisible = true;
      }

      this.createProgramModalVisible = false;
    },
    handleHideGenerativeProgram() {
      this.generativeModalVisible = false;
    },
    handleJoinGenerator() {
      const user = JSON.parse(localStorage.getItem('user'));
      aiSocket.joinGeneratorRoom(user.userId);
    },
    handleLeaveGenerator() {
      const user = JSON.parse(localStorage.getItem('user'));

      aiSocket.leaveGeneratorRoom(user.userId);
    },
    handleGeneratorAction(generateData) {
      this.generativeProgramProcessing = true;
      const user = JSON.parse(localStorage.getItem('user'));

      const tempGenerateData = {
        userId: user.userId,
        ...generateData
      };

      aiService.generateProgram(tempGenerateData).then(res => {
        this.createProgramModalVisible = false;

        this.$snackbar.add({
          text: this.$t('program_generation_is_in_queue'),
          type: 'success'
        });

      }).catch(err => {
        handleErrorResponse(err, this.$snackbar);
      }).finally(() => {
        this.generativeProgramProcessing = false;
        this.generativeModalVisible = false;
        this.transformProgramModalVisible = false;
      });
    },
    checkUrlParams() {
      if (this.$route.query.createNanoCourseModal === 'true') {
        this.createProgramModalVisible = true;

        const query = { ...this.$route.query };
        delete query.createNanoCourseModal;
        this.$router.replace({ query });

        this.$nextTick(() => {
          this.handleCreateAction('genAI');
        });
      } else if (this.$route.query.createProgramModal === 'true') {
        this.createProgramModalVisible = true;
        const query = { ...this.$route.query };
        delete query.createProgramModal;
        this.$router.replace({ query });

      }
    },
  },
  computed: {
    checkGeneratorButton() {
      return this.genStatus && this.$moment(this.genStatus?.updatedAt).isAfter(this.$moment().subtract(1, 'hours'));
    }
  }

}
</script>

<style scoped>
.page-title {
  font-family: euclid_medium, sans-serif;
  font-size: 20px;
  color: #040C2D;
  margin-bottom: 20px;
}
.filter-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.no-results {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
}

.no-results p {
  margin-left: 10px;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #667085;
}

.program-list-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.generator-info-button {
  background-color:  #F2F4F7;
  border: none;
  outline: none;
  border-radius: 9px;
  padding: 10px 20px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #040C2D;
  margin-right: 10px;
}

.generator-info-button:hover {
  background-color: #E5E9F2;
}

.generator-info-button.completed {
  background-color: #00C48C;
  color: #FFF;
}

.generator-info-button.in_progress {
  background-color: #FFB800;
  color: #FFF;
}

@media (max-width: 1024px) {
  .program-list-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .program-list-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}
</style>
